<template>
  <div class="home">
    <!-- 英雄区域 -->
    <section class="hero-section">
      <div class="hero-container">
        <!-- 左侧轮播图 -->
        <div class="carousel-wrapper">
          <div class="carousel" ref="carousel">
            <div class="carousel-inner" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
              <div class="carousel-item" v-for="(img, index) in carouselImages" :key="index">
                <img :src="img.src" :alt="img.alt" class="carousel-image" />
                <div class="carousel-caption">
                  <h3>{{ img.title }}</h3>
                  <p>{{ img.description }}</p>
                </div>
              </div>
            </div>
            <div class="carousel-indicators">
              <span
                v-for="(img, index) in carouselImages"
                :key="index"
                :class="['indicator', { active: currentSlide === index }]"
                @click="currentSlide = index"
              ></span>
            </div>
            <button class="carousel-btn prev" @click="prevSlide">‹</button>
            <button class="carousel-btn next" @click="nextSlide">›</button>
          </div>
        </div>

        <!-- 右侧欢迎区域 -->
        <div class="welcome-section">
          <h1 class="welcome-title">Welcome to AlleleBC</h1>
          <div class="intro-content">
            <p class="intro-paragraph">
              AlleleBC is a comprehensive bioinformatics database focused on allele-specific expression
              and allele-specific methylation research across multiple diseases. We are dedicated to
              collecting, organizing, and analyzing genomic data from around the world, providing
              high-quality data resources and analysis tools for researchers.
            </p>
            <p class="intro-paragraph">
              By integrating advanced bioinformatics technologies and large-scale genomic data,
              AlleleBC provides powerful platform support for exploring disease-related allelic
              variations, expression patterns, and methylation modifications, facilitating the
              development of precision medicine and personalized therapy.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 图表区域 -->
    <section class="charts-section">
      <h2 class="section-title">Data Visualization</h2>
      <div class="charts-container">

        <!-- 图表1: GO生物过程富集分析 -->
        <div class="chart-item">
          <div class="chart-header">
            <h3>GO Biological Process Enrichment</h3>
            <div class="legend-container">
              <div class="color-legend">
                <div class="legend-title">p.adjust</div>
                <div class="color-bar"></div>
                <div class="legend-values">
                  <span>0.01</span>
                  <span>0.03</span>
                </div>
              </div>
              <div class="size-legend">
                <div class="legend-title">Count</div>
                <div class="size-circles">
                  <div class="size-circle small"></div>
                  <span>5</span>
                  <div class="size-circle medium"></div>
                  <span>10</span>
                  <div class="size-circle large"></div>
                  <span>15</span>
                </div>
              </div>
            </div>
          </div>
          <div class="chart-content" id="bp-chart"></div>
        </div>

        <!-- 图表2: GO细胞组分富集分析 -->
        <div class="chart-item">
          <div class="chart-header">
            <h3>GO Cellular Component Enrichment</h3>
            <div class="legend-container">
              <div class="color-legend">
                <div class="legend-title">p.adjust</div>
                <div class="color-bar"></div>
                <div class="legend-values">
                  <span>0.01</span>
                  <span>0.03</span>
                </div>
              </div>
              <div class="size-legend">
                <div class="legend-title">Count</div>
                <div class="size-circles">
                  <div class="size-circle small"></div>
                  <span>5</span>
                  <div class="size-circle medium"></div>
                  <span>10</span>
                  <div class="size-circle large"></div>
                  <span>15</span>
                </div>
              </div>
            </div>
          </div>
          <div class="chart-content" id="cc-chart"></div>
        </div>

        <!-- 图表3: 火山图 -->
        <div class="chart-item">
          <div class="chart-header">
            <h3>Volcano Plot of ASE Genes in CAF1</h3>
            <div class="volcano-legend">
              <div class="legend-item">
                <div class="legend-color down"></div>
                <span>Down</span>
              </div>
              <div class="legend-item">
                <div class="legend-color ns"></div>
                <span>NS</span>
              </div>
              <div class="legend-item">
                <div class="legend-color up"></div>
                <span>Up</span>
              </div>
            </div>
          </div>
          <div class="chart-content" id="volcano-chart"></div>
        </div>

      </div>
    </section>

    <!-- 统计数据区域 -->
    <section class="stats-section">
      <h2 class="section-title">Database Statistics</h2>
      <div class="stats-intro">
        <p>Comprehensive genomic data collection and analysis</p>
      </div>
      <div class="stats-grid">
        <div
          class="stat-card"
          v-for="(stat, index) in statistics"
          :key="stat.name"
        >
          <div class="stat-icon">
            <span class="icon-symbol">{{ stat.icon }}</span>
          </div>
          <div class="stat-content">
            <h3 class="stat-name">{{ stat.displayName }}</h3>
            <div class="stat-number">
              <span class="number" ref="statNumbers">{{ stat.count.toLocaleString() }}</span>
              <span class="unit">records</span>
            </div>
          </div>
          <div class="stat-progress">
            <div class="progress-bar" :style="{ width: `${(stat.count / maxCount) * 100}%` }"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- 出版物区域 -->
    <section class="publications-section">
      <h2 class="section-title">Publications</h2>
      <div class="publications-list">
        <div class="publication-item" v-for="(pub, index) in publications" :key="index">
          <div class="pub-icon">J</div>
          <span class="pub-name">{{ pub }}</span>
        </div>
      </div>
    </section>

    <!-- 工具提示 -->
    <div class="tooltip" id="tooltip"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'

export default {
  name: 'Home',
  data() {
    return {
      currentSlide: 0,
      // 轮播图数据
      // 轮播图数据
      carouselImages: [
        {
          src: require('@/assets/images/GO+KEGG.png'),
          alt: 'GO and KEGG Pathway Analysis',
          title: 'GO & KEGG Pathway Analysis',
          description: 'Comprehensive functional enrichment analysis for gene ontology and pathway mapping'
        },
        {
          src: require('@/assets/images/二项检测.png'),
          alt: 'Binomial Statistical Testing',
          title: 'Binomial Statistical Testing',
          description: 'Rigorous statistical methods for allele-specific expression detection and validation'
        },
        {
          src: require('@/assets/images/分组火山图.png'),
          alt: 'Grouped Volcano Plot Visualization',
          title: 'Grouped Volcano Plot Analysis',
          description: 'Multi-dimensional differential expression visualization with statistical significance mapping'
        }
      ],
      // 统计数据
      statistics: [
        {name: 'variants', displayName: 'Variants', count: 3772738, icon: 'V'},
        {name: 'allele_configurations', displayName: 'Allele Configurations', count: 16458, icon: 'A'},
        {name: 'allelic_counts', displayName: 'Allelic Counts', count: 40386, icon: 'C'},
        {name: 'amr_regions', displayName: 'AMR Regions', count: 2390065, icon: 'R'},
        {name: 'genes', displayName: 'Genes', count: 4693, icon: 'G'},
        {name: 'haplotypes', displayName: 'Haplotypes', count: 34036, icon: 'H'},
        {name: 'haplotype_gene_counts', displayName: 'Haplotype Gene Counts', count: 4721, icon: 'P'},
        {name: 'sequencing_snps', displayName: 'Sequencing SNPs', count: 3822404, icon: 'S'}
      ],
      // 出版物列表
      publications: [
        'Nature Genetics',
        'Cell',
        'Science',
        'Nature Medicine',
        'Genome Research'
      ]
    }
  },
  computed: {
    maxCount() {
      return Math.max(...this.statistics.map(s => s.count))
    }
  },
  methods: {
    // 下一张幻灯片
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.carouselImages.length
    },
    // 上一张幻灯片
    prevSlide() {
      this.currentSlide = this.currentSlide === 0
          ? this.carouselImages.length - 1
          : this.currentSlide - 1
    },
    // 创建GO富集分析图表
    createGOChart(containerId, data, title) {
      const containerWidth = document.querySelector(containerId).clientWidth;
      const margin = {top: 20, right: 30, bottom: 40, left: 200};
      const width = containerWidth - margin.left - margin.right - 40;
      const height = Math.max(250, data.length * 22) - margin.top - margin.bottom;

      // 清空容器
      d3.select(containerId).selectAll("*").remove();

      const svg = d3.select(containerId)
          .append("svg")
          .attr("width", width + margin.left + margin.right)
          .attr("height", height + margin.top + margin.bottom);

      const g = svg.append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);

      // 排序数据
      const sortedData = data.sort((a, b) => b.geneRatio - a.geneRatio);

      // 设置比例尺
      const xScale = d3.scaleLinear()
          .domain([0, d3.max(data, d => d.geneRatio) * 1.1])
          .range([0, width]);

      const yScale = d3.scaleBand()
          .domain(sortedData.map(d => d.term))
          .range([0, height])
          .padding(0.1);

      const colorScale = d3.scaleSequential(d3.interpolateRdYlBu)
          .domain(d3.extent(data, d => d.pAdjust).reverse());

      const sizeScale = d3.scaleSqrt()
          .domain(d3.extent(data, d => d.count))
          .range([4, 15]);

      // 创建坐标轴
      g.append("g")
          .attr("transform", `translate(0,${height})`)
          .call(d3.axisBottom(xScale).tickFormat(d3.format(".3f")));

      g.append("g")
          .call(d3.axisLeft(yScale))
          .selectAll("text")
          .style("font-size", "10px");

      // 创建tooltip
      const tooltip = d3.select("#tooltip");

      // 绘制散点
      g.selectAll(".enrichment-point")
          .data(sortedData)
          .enter()
          .append("circle")
          .attr("class", "enrichment-point")
          .attr("cx", d => xScale(d.geneRatio))
          .attr("cy", d => yScale(d.term) + yScale.bandwidth() / 2)
          .attr("r", d => sizeScale(d.count))
          .attr("fill", d => colorScale(d.pAdjust))
          .attr("stroke", "#333")
          .attr("stroke-width", 0.5)
          .attr("opacity", 0.8)
          .style("cursor", "pointer")
          .on("mouseover", function (event, d) {
            d3.select(this)
                .transition()
                .duration(200)
                .attr("r", sizeScale(d.count) * 1.3);

            tooltip
                .style("opacity", 1)
                .html(`
              <strong>${d.term}</strong><br/>
              GeneRatio: ${d.geneRatio.toFixed(3)}<br/>
              p.adjust: ${d.pAdjust.toFixed(3)}<br/>
              Count: ${d.count}
            `)
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 10) + "px");
          })
          .on("mouseout", function (event, d) {
            d3.select(this)
                .transition()
                .duration(200)
                .attr("r", sizeScale(d.count));

            tooltip.style("opacity", 0);
          });
    },
    // 创建火山图
    createVolcanoChart() {
      const containerWidth = document.querySelector("#volcano-chart").clientWidth;
      const margin = {top: 20, right: 30, bottom: 50, left: 60};
      const width = containerWidth - margin.left - margin.right - 40;
      const height = 300 - margin.top - margin.bottom;

      // 清空容器
      d3.select("#volcano-chart").selectAll("*").remove();

      const svg = d3.select("#volcano-chart")
          .append("svg")
          .attr("width", width + margin.left + margin.right)
          .attr("height", height + margin.top + margin.bottom);

      const g = svg.append("g")
          .attr("transform", `translate(${margin.left},${margin.top})`);

      // 使用真实的火山图数据
      const volcanoData = [
        // 下调基因（蓝色）
        {gene: "CCL11", logFC: -10.5, significance: 7, group: "Down"},
        {gene: "TMEM184B", logFC: -10, significance: 4, group: "Down"},
        {gene: "C4orf3", logFC: -9, significance: 2, group: "Down"},
        {gene: "PABPC4L", logFC: -6, significance: 4, group: "Down"},
        {gene: "UBA3", logFC: -5, significance: 2, group: "Down"},
        {gene: "PYROXD2", logFC: -4.5, significance: 1, group: "Down"},
        {gene: "CTSH", logFC: -4, significance: 1, group: "Down"},
        {gene: "INPP5F", logFC: -3.5, significance: 12, group: "Down"},
        {gene: "RNU5F-1", logFC: -2.5, significance: 5, group: "Down"},

        // 上调基因（橙色）
        {gene: "SET", logFC: 3, significance: 85, group: "Up"},
        {gene: "SLC9A3R2", logFC: 4, significance: 50, group: "Up"},
        {gene: "CCNT1", logFC: 7, significance: 6, group: "Up"},
        {gene: "ARID4B", logFC: 9, significance: 8, group: "Up"},
        {gene: "LETM1", logFC: 2.5, significance: 6, group: "Up"},
        {gene: "ZNF141", logFC: 5.5, significance: 5, group: "Up"},
        {gene: "VPS9D1-AS1", logFC: 8.5, significance: 3, group: "Up"},
        {gene: "SLC39A9", logFC: 5, significance: 2, group: "Up"},
        {gene: "NCK1PSD", logFC: 1.5, significance: 1.5, group: "Up"},
        {gene: "PGM2L1", logFC: 3.5, significance: 2, group: "Up"},
        {gene: "CACNB3", logFC: 6, significance: 1.5, group: "Up"},
        {gene: "PRKD1", logFC: 8, significance: 1, group: "Up"},
        {gene: "NAGLU", logFC: 10, significance: 1, group: "Up"},

        // 添加少量随机点来填充图表
        ...this.generateRandomPoints(80)
      ];

      // 设置比例尺
      const xScale = d3.scaleLinear()
          .domain(d3.extent(volcanoData, d => d.logFC))
          .range([0, width]);

      const yScale = d3.scaleLinear()
          .domain([0, d3.max(volcanoData, d => d.significance)])
          .range([height, 0]);

      const colorScale = d3.scaleOrdinal()
          .domain(["Down", "NS", "Up"])
          .range(["#2196F3", "#CCCCCC", "#FF9800"]);

      // 创建坐标轴
      g.append("g")
          .attr("transform", `translate(0,${height})`)
          .call(d3.axisBottom(xScale));

      g.append("g")
          .call(d3.axisLeft(yScale));

      // 添加坐标轴标签
      g.append("text")
          .attr("class", "axis-label")
          .attr("x", width / 2)
          .attr("y", height + 40)
          .style("text-anchor", "middle")
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .text("log2(Fold Change)");

      g.append("text")
          .attr("class", "axis-label")
          .attr("transform", "rotate(-90)")
          .attr("y", -40)
          .attr("x", -height / 2)
          .style("text-anchor", "middle")
          .style("font-size", "12px")
          .style("font-weight", "bold")
          .text("Significance (-log10 adj p-value)");

      // 添加阈值线
      // 垂直线 (fold change thresholds)
      g.append("line")
          .attr("class", "threshold-line")
          .attr("x1", xScale(-1))
          .attr("x2", xScale(-1))
          .attr("y1", 0)
          .attr("y2", height)
          .style("stroke", "#333")
          .style("stroke-width", 1)
          .style("stroke-dasharray", "5,5")
          .style("opacity", 0.7);

      g.append("line")
          .attr("class", "threshold-line")
          .attr("x1", xScale(1))
          .attr("x2", xScale(1))
          .attr("y1", 0)
          .attr("y2", height)
          .style("stroke", "#333")
          .style("stroke-width", 1)
          .style("stroke-dasharray", "5,5")
          .style("opacity", 0.7);

      // 水平线 (significance threshold)
      g.append("line")
          .attr("class", "threshold-line")
          .attr("x1", 0)
          .attr("x2", width)
          .attr("y1", yScale(2))
          .attr("y2", yScale(2))
          .style("stroke", "#333")
          .style("stroke-width", 1)
          .style("stroke-dasharray", "5,5")
          .style("opacity", 0.7);

      // 创建tooltip
      const tooltip = d3.select("#tooltip");

      // 绘制散点
      const circles = g.selectAll(".gene-point")
          .data(volcanoData)
          .enter()
          .append("circle")
          .attr("class", "gene-point")
          .attr("cx", d => xScale(d.logFC))
          .attr("cy", d => yScale(d.significance))
          .attr("r", d => d.gene.startsWith("Gene_") ? 1.5 : 3)
          .attr("fill", d => colorScale(d.group))
          .attr("opacity", d => d.gene.startsWith("Gene_") ? 0.5 : 0.8)
          .attr("stroke", d => d.gene.startsWith("Gene_") ? "none" : "#333")
          .attr("stroke-width", d => d.gene.startsWith("Gene_") ? 0 : 1)
          .style("cursor", "pointer");

      // 添加基因标签（只为重要基因添加）
      const importantGenes = volcanoData.filter(d => !d.gene.startsWith("Gene_"));

      g.selectAll(".gene-label")
          .data(importantGenes)
          .enter()
          .append("text")
          .attr("class", "gene-label")
          .attr("x", d => xScale(d.logFC))
          .attr("y", d => yScale(d.significance) - 8)
          .style("text-anchor", "middle")
          .style("font-size", "9px")
          .style("fill", "#333")
          .style("font-weight", "bold")
          .style("pointer-events", "none")
          .text(d => d.gene);

      // 添加交互效果
      circles
          .on("mouseover", function (event, d) {
            d3.select(this)
                .transition()
                .duration(200)
                .attr("r", d.gene.startsWith("Gene_") ? 3 : 5)
                .attr("opacity", 1);

            tooltip
                .style("opacity", 1)
                .html(`
              <strong>${d.gene}</strong><br/>
              log2(FC): ${d.logFC.toFixed(2)}<br/>
              -log10(p): ${d.significance.toFixed(2)}<br/>
              Group: ${d.group}
            `)
                .style("left", (event.pageX + 10) + "px")
                .style("top", (event.pageY - 10) + "px");
          })
          .on("mouseout", function (event, d) {
            d3.select(this)
                .transition()
                .duration(200)
                .attr("r", d.gene.startsWith("Gene_") ? 1.5 : 3)
                .attr("opacity", d.gene.startsWith("Gene_") ? 0.5 : 0.8);

            tooltip.style("opacity", 0);
          });
    },
    // 生成随机数据点
    generateRandomPoints(count) {
      const points = [];

      for (let i = 0; i < count; i++) {
        let logFC, significance, group;

        // 大部分点集中在-3到3之间，0到2之间（灰色区域）
        if (Math.random() < 0.85) { // 85%的点在无显著性区域
          logFC = (Math.random() - 0.5) * 6; // -3 到 3
          significance = Math.random() * 2; // 0 到 2
          group = "NS";
        } else { // 15%的点在其他区域
          logFC = (Math.random() - 0.5) * 16; // -8 到 8
          significance = Math.random() * 8;

          if (Math.abs(logFC) < 1 || significance < 2) {
            group = "NS";
          } else if (logFC < -1) {
            group = "Down";
          } else {
            group = "Up";
          }
        }

        points.push({
          gene: `Gene_${i}`,
          logFC: logFC,
          significance: significance,
          group: group
        });
      }
      return points;
    }
  },
  mounted() {
    // 自动轮播
    setInterval(() => {
      this.nextSlide()
    }, 5000)

    // 等待DOM渲染完成后创建图表
    this.$nextTick(() => {
      // GO生物过程数据
      const bpData = [
        {
          term: "peripheral nervous system axon regeneration",
          category: "BP",
          geneRatio: 0.025,
          pAdjust: 0.025,
          count: 5
        },
        {
          term: "positive regulation of focal adhesion assembly",
          category: "BP",
          geneRatio: 0.028,
          pAdjust: 0.023,
          count: 6
        },
        {term: "integrin activation", category: "BP", geneRatio: 0.032, pAdjust: 0.021, count: 7},
        {term: "cellular response to amino acid stimulus", category: "BP", geneRatio: 0.035, pAdjust: 0.019, count: 8},
        {term: "regulation of cell shape", category: "BP", geneRatio: 0.042, pAdjust: 0.017, count: 10},
        {term: "integrin-mediated signaling pathway", category: "BP", geneRatio: 0.045, pAdjust: 0.016, count: 11},
        {term: "extracellular matrix organization", category: "BP", geneRatio: 0.082, pAdjust: 0.013, count: 17}
      ];

      // GO细胞组分数据
      const ccData = [
        {term: "banded collagen fibril", category: "CC", geneRatio: 0.028, pAdjust: 0.022, count: 6},
        {term: "fibrillar collagen trimer", category: "CC", geneRatio: 0.032, pAdjust: 0.020, count: 7},
        {term: "collagen trimer", category: "CC", geneRatio: 0.048, pAdjust: 0.016, count: 11},
        {term: "basement membrane", category: "CC", geneRatio: 0.055, pAdjust: 0.015, count: 12},
        {term: "cell-substrate junction", category: "CC", geneRatio: 0.085, pAdjust: 0.014, count: 16},
        {term: "focal adhesion", category: "CC", geneRatio: 0.088, pAdjust: 0.013, count: 17},
        {term: "collagen-containing extracellular matrix", category: "CC", geneRatio: 0.115, pAdjust: 0.011, count: 21}
      ];

      // 创建图表
      this.createGOChart("#bp-chart", bpData, "GO Biological Process");
      this.createGOChart("#cc-chart", ccData, "GO Cellular Component");
      this.createVolcanoChart();
    });
  }
}
</script>

<style scoped>
.home {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 英雄区域样式 */
.hero-section {
  padding: 60px 20px;
  background: white;
}

.hero-container {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

/* 轮播图样式 */
.carousel-wrapper {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.1);
}

.carousel {
  position: relative;
  width: 100%;
  height: 400px;
}

.carousel-inner {
  display: flex;
  height: 100%;
  transition: transform 0.5s ease;
}

.carousel-item {
  min-width: 100%;
  height: 100%;
  position: relative;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(44, 62, 80, 0.8));
  color: white;
  padding: 40px 30px 30px;
}

.carousel-caption h3 {
  font-size: 24px;
  margin-bottom: 8px;
  font-weight: 600;
}

.carousel-caption p {
  font-size: 16px;
  opacity: 0.9;
}

.carousel-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;
}

.indicator {
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
}

.indicator.active {
  background: white;
  transform: scale(1.2);
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(44, 62, 80, 0.7);
  color: white;
  border: none;
  font-size: 24px;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 2;
}

.carousel-btn:hover {
  background: rgba(44, 62, 80, 0.9);
}

.carousel-btn.prev {
  left: 15px;
}

.carousel-btn.next {
  right: 15px;
}

/* 欢迎区域样式 */
.welcome-section {
  animation: fadeIn 1s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.welcome-title {
  font-size: 42px;
  margin-bottom: 30px;
  color: #2c3e50;
  font-weight: 700;
}

.intro-content {
  font-size: 16px;
  line-height: 1.7;
  color: #666;
}

.intro-paragraph {
  margin-bottom: 20px;
}

/* 图表区域样式 */
.charts-section {
  padding: 80px 20px;
  background: #f8f9fa;
}

.section-title {
  text-align: center;
  font-size: 32px;
  margin-bottom: 50px;
  color: #2c3e50;
  font-weight: 600;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  border-radius: 2px;
}

.charts-container {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.chart-item {
  background: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 4px 16px rgba(44, 62, 80, 0.1);
  transition: transform 0.3s ease;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.chart-item:hover {
  transform: translateY(-5px);
}

.chart-header {
  margin-bottom: 15px;
  flex-shrink: 0;
}

.chart-header h3 {
  color: #2c3e50;
  font-size: 16px;
  margin-bottom: 10px;
  text-align: center;
}

.chart-content {
  flex: 1;
  overflow: hidden;
  min-height: 300px;
}

/* 图例样式 */
.legend-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 11px;
}

.color-legend, .size-legend {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.legend-title {
  font-weight: bold;
  margin-bottom: 3px;
}

.color-bar {
  width: 10px;
  height: 50px;
  background: linear-gradient(to bottom, #2196F3, #673AB7, #9C27B0, #E91E63, #F44336);
  border: 1px solid #ccc;
  margin-bottom: 3px;
}

.legend-values {
  display: flex;
  justify-content: space-between;
  width: 10px;
  font-size: 9px;
}

.size-circles {
  display: flex;
  gap: 3px;
  align-items: center;
}

.size-circle {
  border: 1px solid #666;
  border-radius: 50%;
  background: rgba(100, 100, 100, 0.3);
}

.size-circle.small {
  width: 5px;
  height: 5px;
}

.size-circle.medium {
  width: 8px;
  height: 8px;
}

.size-circle.large {
  width: 12px;
  height: 12px;
}

/* 火山图图例 */
.volcano-legend {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.legend-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.legend-color.down {
  background: #2196F3;
}

.legend-color.ns {
  background: #CCCCCC;
}

.legend-color.up {
  background: #FF9800;
}

/* 工具提示样式 */
.tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 300px;
  z-index: 1000;
}

/* 统计数据区域样式 */
.stats-section {
  padding: 80px 20px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
}

.stats-section .section-title {
  color: white;
}

.stats-section .section-title::after {
  background: white;
}

.stats-intro {
  text-align: center;
  margin-bottom: 50px;
  font-size: 18px;
  opacity: 0.9;
}

.stats-grid {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 30px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.15);
}

.stat-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.icon-symbol {
  font-size: 24px;
  font-weight: bold;
}

.stat-name {
  font-size: 16px;
  margin-bottom: 10px;
  opacity: 0.9;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 15px;
}

.number {
  margin-right: 8px;
}

.unit {
  font-size: 14px;
  opacity: 0.8;
}

.stat-progress {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: white;
  border-radius: 2px;
  transition: width 1s ease;
}

/* 出版物区域样式 */
.publications-section {
  padding: 80px 20px;
  background: white;
}

.publications-list {
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.publication-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px 30px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.publication-item:hover {
  transform: translateX(10px);
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
}

.pub-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
}

.publication-item:hover .pub-icon {
  background: white;
  color: #2c3e50;
}

.pub-name {
  font-size: 18px;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .charts-container {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .chart-item {
    min-height: 350px;
  }
}

@media (max-width: 1024px) {
  .hero-container {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .charts-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {
  .welcome-title {
    font-size: 32px;
  }

  .carousel {
    height: 300px;
  }

  .section-title {
    font-size: 28px;
  }

  .stat-number {
    font-size: 24px;
  }

  .hero-section {
    padding: 40px 20px;
  }

  .charts-section,
  .stats-section,
  .publications-section {
    padding: 60px 20px;
  }
}

@media (max-width: 480px) {
  .carousel-caption {
    padding: 20px 15px 15px;
  }

  .carousel-caption h3 {
    font-size: 18px;
  }

  .carousel-caption p {
    font-size: 14px;
  }
}
</style>